import React, { useState, useCallback, useMemo } from 'react';
import { Tab, useStore, HttpResponse } from '../../stores';
import { useAuth } from '../../lib/auth/AuthProvider';
import { Send, Loader2, Save, Terminal, Check, Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import { KeyValueEditor } from './KeyValueEditor';
import { BodyEditor } from './BodyEditor';
import { PathParamsEditor, extractPathParams, syncPathParamsWithUrl } from './PathParamsEditor';
import { OAuth2Auth, HawkAuth } from './auth';
import { ScriptEditor } from './ScriptEditor';
import { interpolateRequest, buildVariableContext, saveToHistory, extractVariables } from '../../lib/http';
import { isElectron } from '../../lib/http/httpClient';
import { generateHawkHeader } from '../../lib/auth/hawk';
import { resolveAuth, getAuthTypeLabel, isUsingInheritedAuth } from '../../lib/auth/authResolution';
import { executePreRequestScript, executePostRequestScript } from '../../lib/scripting';
import { VariableQuickLook } from '../variables';
import { SaveToCollectionDialog } from '../collections';
import { copyCurlToClipboard } from '../../lib/export';
import { useKeyboardShortcuts } from '../../hooks';
import { useCollections } from '../../hooks/useCollections';

interface RequestBuilderProps {
  tab: Tab;
  workspaceId?: string;
}

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'] as const;

// Default workspace ID for local history when no workspace is selected
const DEFAULT_LOCAL_WORKSPACE = 'local-workspace';

export function RequestBuilder({ tab, workspaceId }: RequestBuilderProps) {
  const { updateTabRequest, updateTab, setTabLoading, setTabResponse, environments, activeEnvironmentId, activeWorkspaceId, collections, closeTab } = useStore();
  const { saveRequest } = useCollections();
  // Use provided workspaceId, or active workspace, or default to local workspace for history
  const effectiveWorkspaceId = workspaceId || activeWorkspaceId || DEFAULT_LOCAL_WORKSPACE;
  const { config, getAccessToken } = useAuth();
  const [activeSection, setActiveSection] = useState<'params' | 'path' | 'headers' | 'body' | 'auth' | 'pre-script' | 'post-script'>('params');

  // Find parent collection for this request (if any)
  const parentCollection = useMemo(() => {
    if (!tab.collectionId) return null;
    return collections.find(c => c.id === tab.collectionId) || null;
  }, [tab.collectionId, collections]);

  // Check if using inherited auth
  const usingInheritedAuth = useMemo(() => {
    return isUsingInheritedAuth(tab.request, parentCollection);
  }, [tab.request, parentCollection]);

  // Compute headers that will be added from auth configuration
  const computedHeaders = useMemo(() => {
    const headers: { key: string; value: string; source: string }[] = [];

    // Resolve the auth (handle inheritance)
    const resolvedAuthResult = resolveAuth(tab.request, parentCollection);
    const auth = resolvedAuthResult.auth;
    const isFromCollection = resolvedAuthResult.source === 'collection';
    const source = isFromCollection ? `${getAuthTypeLabel(auth?.type)} (from collection)` : getAuthTypeLabel(auth?.type);

    if (auth?.type === 'bearer' && auth.bearer?.token) {
      const displayToken = auth.bearer.token.length > 20
        ? `${auth.bearer.token.substring(0, 20)}...`
        : auth.bearer.token;
      headers.push({ key: 'Authorization', value: `Bearer ${displayToken}`, source });
    } else if (auth?.type === 'basic' && auth.basic?.username) {
      headers.push({ key: 'Authorization', value: `Basic ••••••••`, source });
    } else if (auth?.type === 'api-key' && auth.apiKey?.addTo === 'header' && auth.apiKey.key) {
      const displayValue = auth.apiKey.value.length > 20
        ? `${auth.apiKey.value.substring(0, 20)}...`
        : auth.apiKey.value;
      headers.push({ key: auth.apiKey.key, value: displayValue, source });
    } else if (auth?.type === 'oauth2' && auth.oauth2?.accessToken) {
      const displayToken = auth.oauth2.accessToken.length > 20
        ? `${auth.oauth2.accessToken.substring(0, 20)}...`
        : auth.oauth2.accessToken;
      headers.push({ key: 'Authorization', value: `Bearer ${displayToken}`, source });
    } else if (auth?.type === 'hawk' && auth.hawk?.authId) {
      // HAWK header is computed at send time with a timestamp
      headers.push({ key: 'Authorization', value: '(computed at send time)', source });
    }

    return headers;
  }, [tab.request, parentCollection]);

  // Extract path params from URL for badge count
  const urlPathParams = extractPathParams(tab.request.url);
  const hasPathParams = urlPathParams.length > 0;
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [curlCopied, setCurlCopied] = useState(false);

  // Handle copy cURL to clipboard
  const handleCopyCurl = async () => {
    await copyCurlToClipboard(tab.request);
    setCurlCopied(true);
    setTimeout(() => setCurlCopied(false), 2000);
  };

  const methodColors: Record<string, string> = {
    GET: 'bg-blue-500',
    POST: 'bg-green-500',
    PUT: 'bg-orange-500',
    PATCH: 'bg-cyan-500',
    DELETE: 'bg-red-500',
    HEAD: 'bg-purple-500',
    OPTIONS: 'bg-blue-600',
  };

  const handleSend = async () => {
    if (!tab.request.url) return;

    setTabLoading(tab.id, true);

    try {
      // Get active environment and global environment for variable interpolation
      const activeEnvironment = environments.find(e => e.id === activeEnvironmentId);
      const globalEnvironment = environments.find(e => e.isGlobal);

      // Build variable context with collection variables
      const variableContext = buildVariableContext(
        activeEnvironment,
        globalEnvironment,
        parentCollection?.variables
      );

      // Resolve auth (inherit from collection if needed)
      const resolvedAuthResult = resolveAuth(tab.request, parentCollection);
      const requestWithResolvedAuth = {
        ...tab.request,
        auth: resolvedAuthResult.auth as typeof tab.request.auth,
      };

      // Execute pre-request script FIRST (before interpolation)
      // This allows scripts to set variables that will be interpolated into the request
      if (tab.request.scripts?.preRequest) {
        const preScriptResult = await executePreRequestScript(tab.request.scripts.preRequest, {
          request: requestWithResolvedAuth, // Pass uninterpolated request
          environment: activeEnvironment || undefined,
          globalEnvironment: globalEnvironment || undefined,
        });

        // Log any console output
        preScriptResult.logs.forEach(({ level, args }) => {
          console[level as 'log' | 'info' | 'warn' | 'error']('[Pre-request]', ...args);
        });

        if (!preScriptResult.success) {
          console.error('Pre-request script error:', preScriptResult.error);
        }

        // Merge script variable updates into context
        // This ensures variables set via pm.environment.set() are available for interpolation
        if (preScriptResult.variableUpdates?.environment) {
          for (const [key, value] of Object.entries(preScriptResult.variableUpdates.environment)) {
            // Add as environment variable with highest priority
            if (!variableContext.environment) {
              variableContext.environment = [];
            }
            // Check if variable already exists
            const existingIndex = variableContext.environment.findIndex(v => v.key === key);
            if (existingIndex >= 0) {
              variableContext.environment[existingIndex].value = value;
            } else {
              variableContext.environment.push({
                key,
                value,
                enabled: true,
                isSecret: false,
              });
            }
          }
        }

        // Apply modified headers from script (if any)
        if (preScriptResult.modifiedRequest?.headers) {
          requestWithResolvedAuth.headers = preScriptResult.modifiedRequest.headers;
        }
      }

      // NOW interpolate the request (after script has set variables)
      let interpolatedRequest = interpolateRequest(requestWithResolvedAuth, variableContext);

      // Build headers from interpolated request
      const requestHeaders: Record<string, string> = {};
      interpolatedRequest.headers
        .filter((h) => h.enabled && h.key)
        .forEach((h) => {
          requestHeaders[h.key] = h.value;
        });

      // Add auth headers from interpolated request
      if (interpolatedRequest.auth?.type === 'bearer' && interpolatedRequest.auth.bearer?.token) {
        requestHeaders['Authorization'] = `Bearer ${interpolatedRequest.auth.bearer.token}`;
      } else if (interpolatedRequest.auth?.type === 'basic' && interpolatedRequest.auth.basic) {
        const { username, password } = interpolatedRequest.auth.basic;
        requestHeaders['Authorization'] = `Basic ${btoa(`${username}:${password}`)}`;
      } else if (interpolatedRequest.auth?.type === 'api-key' && interpolatedRequest.auth.apiKey) {
        const { key, value, addTo } = interpolatedRequest.auth.apiKey;
        if (addTo === 'header') {
          requestHeaders[key] = value;
        }
      } else if (interpolatedRequest.auth?.type === 'oauth2' && interpolatedRequest.auth.oauth2?.accessToken) {
        requestHeaders['Authorization'] = `Bearer ${interpolatedRequest.auth.oauth2.accessToken}`;
      }
      // Note: HAWK auth is handled after we have the final URL and body

      // Build URL with params from interpolated request
      let url = interpolatedRequest.url;
      const enabledParams = interpolatedRequest.params.filter((p) => p.enabled && p.key);
      if (enabledParams.length > 0) {
        const searchParams = new URLSearchParams();
        enabledParams.forEach((p) => searchParams.append(p.key, p.value));

        // Add API key param if configured
        if (interpolatedRequest.auth?.type === 'api-key' && interpolatedRequest.auth.apiKey?.addTo === 'query') {
          searchParams.append(interpolatedRequest.auth.apiKey.key, interpolatedRequest.auth.apiKey.value);
        }

        url = `${url}${url.includes('?') ? '&' : '?'}${searchParams.toString()}`;
      }

      // Prepare request body from interpolated request
      let body: string | undefined;
      if (interpolatedRequest.body?.type === 'raw' && interpolatedRequest.body.raw?.content) {
        body = interpolatedRequest.body.raw.content;
        if (!requestHeaders['Content-Type']) {
          const contentTypes: Record<string, string> = {
            json: 'application/json',
            xml: 'application/xml',
            text: 'text/plain',
          };
          requestHeaders['Content-Type'] = contentTypes[interpolatedRequest.body.raw.contentType] || 'text/plain';
        }
      }

      // Generate HAWK auth header (needs final URL and body)
      if (interpolatedRequest.auth?.type === 'hawk' && interpolatedRequest.auth.hawk) {
        const hawkConfig = interpolatedRequest.auth.hawk;
        if (hawkConfig.authId && hawkConfig.authKey) {
          try {
            const hawkHeader = await generateHawkHeader(hawkConfig, {
              method: interpolatedRequest.method,
              url,
              body,
              contentType: requestHeaders['Content-Type'],
            });
            requestHeaders['Authorization'] = hawkHeader;
          } catch (err) {
            console.error('Failed to generate HAWK header:', err);
          }
        }
      }

      let responseData: HttpResponse;

      if (isElectron()) {
        // Electron: send directly via IPC (bypasses CORS)
        const result = await (window as any).snailmanAPI.httpRequest({
          method: interpolatedRequest.method,
          url,
          headers: requestHeaders,
          body: ['GET', 'HEAD'].includes(interpolatedRequest.method) ? undefined : body,
          timeout: 30000,
        });

        responseData = {
          status: result.status,
          statusText: result.statusText,
          headers: result.headers,
          body: result.body,
          size: new TextEncoder().encode(result.body).length,
          time: result.responseTime,
        };

        if (result.error) {
          responseData = {
            status: 0,
            statusText: 'Error',
            headers: {},
            body: `Request failed: ${result.error}`,
            size: 0,
            time: result.responseTime || 0,
          };
        }
      } else {
        // Web: send via Lambda proxy
        const accessToken = await getAccessToken();
        const proxyUrl = config?.api?.endpoint
          ? `${config.api.endpoint}/proxy`
          : '/api/proxy';

        const response = await fetch(proxyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            method: interpolatedRequest.method,
            url,
            headers: requestHeaders,
            body,
          }),
        });

        const result = await response.json();

        if (result.success && result.data) {
          responseData = result.data;
        } else {
          responseData = {
            status: 0,
            statusText: 'Error',
            headers: {},
            body: result.error?.message || 'Request failed',
            size: 0,
            time: 0,
          };
        }
      }

      setTabResponse(tab.id, responseData);

      if (responseData.status !== 0) {
        // Execute post-request script (tests)
        if (tab.request.scripts?.postRequest) {
          const postScriptResult = await executePostRequestScript(tab.request.scripts.postRequest, {
            request: interpolatedRequest,
            response: responseData,
            environment: activeEnvironment || undefined,
            globalEnvironment: globalEnvironment || undefined,
          });

          // Log console output
          postScriptResult.logs.forEach(({ level, args }) => {
            console[level as 'log' | 'info' | 'warn' | 'error']('[Tests]', ...args);
          });

          // Log test results
          if (postScriptResult.tests.length > 0) {
            console.group('Test Results');
            postScriptResult.tests.forEach((test) => {
              if (test.passed) {
                console.log(`%c PASS %c ${test.name}`, 'background: green; color: white; padding: 2px 4px; border-radius: 2px;', '');
              } else {
                console.log(`%c FAIL %c ${test.name}: ${test.error}`, 'background: red; color: white; padding: 2px 4px; border-radius: 2px;', '');
              }
            });
            console.groupEnd();
          }

          if (!postScriptResult.success) {
            console.error('Post-request script error:', postScriptResult.error);
          }
        }

        // Save to history
        if (effectiveWorkspaceId) {
          try {
            await saveToHistory({
              request: tab.request,
              response: responseData,
              workspaceId: effectiveWorkspaceId,
              environmentId: activeEnvironmentId || undefined,
              requestId: tab.requestId,
            });
          } catch (historyError) {
            console.error('Failed to save to history:', historyError);
          }
        }
      } else {
        // Save error to history
        if (effectiveWorkspaceId) {
          try {
            await saveToHistory({
              request: tab.request,
              error: { message: responseData.body || 'Request failed' },
              workspaceId: effectiveWorkspaceId,
              environmentId: activeEnvironmentId || undefined,
              requestId: tab.requestId,
            });
          } catch (historyError) {
            console.error('Failed to save to history:', historyError);
          }
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Request failed';
      const errorResponse: HttpResponse = {
        status: 0,
        statusText: 'Error',
        headers: {},
        body: errorMessage,
        size: 0,
        time: 0,
      };
      setTabResponse(tab.id, errorResponse);

      // Save error to history
      if (effectiveWorkspaceId) {
        try {
          await saveToHistory({
            request: tab.request,
            error: { message: errorMessage },
            workspaceId: effectiveWorkspaceId,
            environmentId: activeEnvironmentId || undefined,
            requestId: tab.requestId,
          });
        } catch (historyError) {
          console.error('Failed to save to history:', historyError);
        }
      }
    } finally {
      setTabLoading(tab.id, false);
    }
  };

  // Format JSON body content
  const handleFormatJson = useCallback(() => {
    if (tab.request.body?.type === 'raw' && tab.request.body.raw?.contentType === 'json') {
      try {
        const content = tab.request.body.raw.content || '';
        if (content.trim()) {
          const formatted = JSON.stringify(JSON.parse(content), null, 2);
          updateTabRequest(tab.id, {
            body: {
              ...tab.request.body,
              raw: {
                ...tab.request.body.raw,
                content: formatted,
              },
            },
          });
        }
      } catch {
        // Invalid JSON - do nothing
      }
    }
  }, [tab.id, tab.request.body, updateTabRequest]);

  // Save handler - save directly for existing requests, show dialog for new ones
  const handleSave = useCallback(async () => {
    // If request is already saved (has collectionId and requestId), save directly
    if (tab.collectionId && tab.requestId) {
      // Update via API + store
      await saveRequest(tab.requestId, tab.collectionId, tab.request);
      // Mark tab as not dirty
      updateTab(tab.id, { isDirty: false });
    } else {
      // Open save dialog for new requests
      setSaveDialogOpen(true);
    }
  }, [tab.collectionId, tab.requestId, tab.request, tab.id, updateTab, saveRequest]);

  // Keyboard shortcuts: Cmd/Ctrl+S to save, Cmd/Ctrl+Enter to send, Cmd/Ctrl+Shift+F to format, Cmd/Ctrl+W to close tab
  useKeyboardShortcuts({
    onSave: handleSave,
    onSend: () => {
      if (tab.request.url && !tab.isLoading) {
        handleSend();
      }
    },
    onFormatJson: handleFormatJson,
    onCloseTab: () => closeTab(tab.id),
  });

  return (
    <div className="h-full flex flex-col bg-background">
      {/* URL Bar */}
      <div className="flex items-center gap-2 p-2 border-b border-border/50">
        {/* Method Selector */}
        <select
          value={tab.request.method}
          onChange={(e) => updateTabRequest(tab.id, { method: e.target.value as any })}
          className={cn(
            'px-2 py-1.5 rounded-md text-white font-medium text-xs border border-transparent cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring',
            methodColors[tab.request.method] || 'bg-gray-500'
          )}
        >
          {HTTP_METHODS.map((method) => (
            <option key={method} value={method}>
              {method}
            </option>
          ))}
        </select>

        {/* URL Input */}
        <input
          type="text"
          value={tab.request.url}
          onChange={(e) => updateTabRequest(tab.id, { url: e.target.value })}
          placeholder="Enter request URL"
          className="flex-1 px-2 py-1.5 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring font-mono text-xs"
        />

        {/* Save Button */}
        <button
          onClick={handleSave}
          className="px-2 py-1.5 bg-muted text-foreground rounded-md hover:bg-accent transition-colors flex items-center gap-1.5"
          title={tab.collectionId && tab.requestId ? "Save (Cmd+S)" : "Save to Collection (Cmd+S)"}
        >
          <Save className="w-3.5 h-3.5" />
        </button>

        {/* Copy cURL Button */}
        <button
          onClick={handleCopyCurl}
          className="px-2 py-1.5 bg-muted text-foreground rounded-md hover:bg-accent transition-colors flex items-center gap-1.5"
          title="Copy as cURL"
        >
          {curlCopied ? (
            <Check className="w-3.5 h-3.5 text-green-500" />
          ) : (
            <Terminal className="w-3.5 h-3.5" />
          )}
        </button>

        {/* Send Button */}
        <button
          onClick={handleSend}
          disabled={tab.isLoading || !tab.request.url}
          className="px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm"
        >
          {tab.isLoading ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-3.5 h-3.5" />
              Send
            </>
          )}
        </button>
      </div>

      {/* Variable Preview - shows when URL contains {{variables}} */}
      {tab.request.url && extractVariables(tab.request.url).length > 0 && (
        <div className="px-2 py-1 bg-muted/30 border-b border-border/50">
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <span>Preview:</span>
            <VariableQuickLook
              text={tab.request.url}
              className="font-mono text-foreground"
            />
          </div>
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex items-center border-b border-border/50">
        {(['params', 'path', 'headers', 'body', 'auth', 'pre-script', 'post-script'] as const).map((section) => {
          // Hide path tab if no path params in URL
          if (section === 'path' && !hasPathParams) return null;

          const hasPreScript = tab.request.scripts?.preRequest;
          const hasPostScript = tab.request.scripts?.postRequest;

          // Display label for section
          const getLabel = () => {
            if (section === 'path') return 'Path';
            if (section === 'pre-script') return 'Pre-request';
            if (section === 'post-script') return 'Tests';
            return section;
          };

          return (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium border-b-2 transition-colors capitalize',
                activeSection === section
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {getLabel()}
              {section === 'params' && tab.request.params.filter((p) => p.enabled).length > 0 && (
                <span className="ml-1 px-1 py-0.5 text-[10px] bg-muted rounded">
                  {tab.request.params.filter((p) => p.enabled).length}
                </span>
              )}
              {section === 'path' && hasPathParams && (
                <span className="ml-1 px-1 py-0.5 text-[10px] bg-muted rounded">
                  {urlPathParams.length}
                </span>
              )}
              {section === 'headers' && tab.request.headers.filter((h) => h.enabled).length > 0 && (
                <span className="ml-1 px-1 py-0.5 text-[10px] bg-muted rounded">
                  {tab.request.headers.filter((h) => h.enabled).length}
                </span>
              )}
              {section === 'pre-script' && hasPreScript && (
                <span className="ml-1 w-1.5 h-1.5 bg-primary rounded-full inline-block" />
              )}
              {section === 'post-script' && hasPostScript && (
                <span className="ml-1 w-1.5 h-1.5 bg-primary rounded-full inline-block" />
              )}
            </button>
          );
        })}
      </div>

      {/* Section Content */}
      <div className="flex-1 overflow-auto p-2">
        {activeSection === 'params' && (
          <KeyValueEditor
            items={tab.request.params}
            onChange={(params) => updateTabRequest(tab.id, { params })}
            placeholder={{ key: 'Parameter name', value: 'Value' }}
          />
        )}

        {activeSection === 'path' && (
          <PathParamsEditor
            url={tab.request.url}
            pathParams={tab.request.pathParams || []}
            onChange={(pathParams) => updateTabRequest(tab.id, { pathParams })}
          />
        )}

        {activeSection === 'headers' && (
          <div className="space-y-4">
            {/* Computed Headers (read-only) from auth configuration */}
            {computedHeaders.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Computed Headers (from Auth)
                </p>
                <div className="space-y-1 bg-muted/30 rounded-md p-2">
                  {computedHeaders.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="font-mono text-foreground font-medium">{h.key}:</span>
                      <span className="font-mono text-muted-foreground flex-1 truncate">{h.value}</span>
                      <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded whitespace-nowrap">{h.source}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* User-defined Headers */}
            <KeyValueEditor
              items={tab.request.headers}
              onChange={(headers) => updateTabRequest(tab.id, { headers })}
              placeholder={{ key: 'Header name', value: 'Value' }}
            />
          </div>
        )}

        {activeSection === 'body' && (
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              {(['none', 'raw'] as const).map((type) => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bodyType"
                    checked={tab.request.body?.type === type || (!tab.request.body && type === 'none')}
                    onChange={() => updateTabRequest(tab.id, { body: { type } })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm capitalize">{type}</span>
                </label>
              ))}
            </div>

            {tab.request.body?.type === 'raw' && (
              <BodyEditor
                content={tab.request.body.raw?.content || ''}
                contentType={tab.request.body.raw?.contentType || 'json'}
                onChange={(content) =>
                  updateTabRequest(tab.id, {
                    body: {
                      ...tab.request.body!,
                      raw: {
                        content,
                        contentType: tab.request.body!.raw?.contentType || 'json',
                      },
                    },
                  })
                }
                onContentTypeChange={(contentType) =>
                  updateTabRequest(tab.id, {
                    body: {
                      ...tab.request.body!,
                      raw: {
                        content: tab.request.body!.raw?.content || '',
                        contentType,
                      },
                    },
                  })
                }
              />
            )}
          </div>
        )}

        {activeSection === 'auth' && (
          <div className="space-y-4">
            <select
              value={tab.request.auth?.type || 'inherit'}
              onChange={(e) =>
                updateTabRequest(tab.id, { auth: { type: e.target.value as any } })
              }
              className="px-3 py-2 bg-background border border-input rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="inherit">Inherit from Collection</option>
              <option value="none">No Auth</option>
              <option value="bearer">Bearer Token</option>
              <option value="basic">Basic Auth</option>
              <option value="api-key">API Key</option>
              <option value="oauth2">OAuth 2.0</option>
              <option value="hawk">Hawk</option>
            </select>

            {/* Inheritance indicator */}
            {(tab.request.auth?.type === 'inherit' || !tab.request.auth?.type) && (
              <div className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-md text-sm",
                usingInheritedAuth
                  ? "bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800"
                  : "bg-muted border border-border"
              )}>
                <Info className={cn("w-4 h-4", usingInheritedAuth ? "text-blue-500" : "text-muted-foreground")} />
                {usingInheritedAuth ? (
                  <span>
                    Using <strong>{getAuthTypeLabel(parentCollection?.auth?.type)}</strong> auth from collection
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    {parentCollection
                      ? "Collection has no auth configured - requests will have no authorization"
                      : "Request is not in a collection - no auth will be used"}
                  </span>
                )}
              </div>
            )}

            {tab.request.auth?.type === 'bearer' && (
              <div>
                <label className="block text-sm font-medium mb-1">Token</label>
                <input
                  type="text"
                  value={tab.request.auth.bearer?.token || ''}
                  onChange={(e) =>
                    updateTabRequest(tab.id, {
                      auth: { ...tab.request.auth!, bearer: { token: e.target.value } },
                    })
                  }
                  placeholder="Bearer token"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md font-mono text-sm"
                />
              </div>
            )}

            {tab.request.auth?.type === 'basic' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Username</label>
                  <input
                    type="text"
                    value={tab.request.auth.basic?.username || ''}
                    onChange={(e) =>
                      updateTabRequest(tab.id, {
                        auth: {
                          ...tab.request.auth!,
                          basic: {
                            username: e.target.value,
                            password: tab.request.auth!.basic?.password || ''
                          },
                        },
                      })
                    }
                    className="w-full px-3 py-2 bg-background border border-input rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Password</label>
                  <input
                    type="password"
                    value={tab.request.auth.basic?.password || ''}
                    onChange={(e) =>
                      updateTabRequest(tab.id, {
                        auth: {
                          ...tab.request.auth!,
                          basic: {
                            username: tab.request.auth!.basic?.username || '',
                            password: e.target.value
                          },
                        },
                      })
                    }
                    className="w-full px-3 py-2 bg-background border border-input rounded-md"
                  />
                </div>
              </div>
            )}

            {tab.request.auth?.type === 'api-key' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Key</label>
                  <input
                    type="text"
                    value={tab.request.auth.apiKey?.key || ''}
                    onChange={(e) =>
                      updateTabRequest(tab.id, {
                        auth: {
                          ...tab.request.auth!,
                          apiKey: {
                            key: e.target.value,
                            value: tab.request.auth!.apiKey?.value || '',
                            addTo: tab.request.auth!.apiKey?.addTo || 'header'
                          },
                        },
                      })
                    }
                    className="w-full px-3 py-2 bg-background border border-input rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Value</label>
                  <input
                    type="text"
                    value={tab.request.auth.apiKey?.value || ''}
                    onChange={(e) =>
                      updateTabRequest(tab.id, {
                        auth: {
                          ...tab.request.auth!,
                          apiKey: {
                            key: tab.request.auth!.apiKey?.key || '',
                            value: e.target.value,
                            addTo: tab.request.auth!.apiKey?.addTo || 'header'
                          },
                        },
                      })
                    }
                    className="w-full px-3 py-2 bg-background border border-input rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Add to</label>
                  <select
                    value={tab.request.auth.apiKey?.addTo || 'header'}
                    onChange={(e) =>
                      updateTabRequest(tab.id, {
                        auth: {
                          ...tab.request.auth!,
                          apiKey: {
                            key: tab.request.auth!.apiKey?.key || '',
                            value: tab.request.auth!.apiKey?.value || '',
                            addTo: e.target.value as 'header' | 'query'
                          },
                        },
                      })
                    }
                    className="px-3 py-2 bg-background border border-input rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="header">Header</option>
                    <option value="query">Query Params</option>
                  </select>
                </div>
              </div>
            )}

            {tab.request.auth?.type === 'oauth2' && (
              <OAuth2Auth
                config={tab.request.auth.oauth2 || {
                  grantType: 'client_credentials',
                  accessTokenUrl: '',
                  clientId: '',
                }}
                onChange={(oauth2) =>
                  updateTabRequest(tab.id, {
                    auth: { ...tab.request.auth!, oauth2 },
                  })
                }
              />
            )}

            {tab.request.auth?.type === 'hawk' && (
              <HawkAuth
                config={tab.request.auth.hawk || {
                  authId: '',
                  authKey: '',
                  algorithm: 'sha256',
                  includePayloadHash: false,
                }}
                onChange={(hawk) =>
                  updateTabRequest(tab.id, {
                    auth: { ...tab.request.auth!, hawk },
                  })
                }
              />
            )}
          </div>
        )}

        {activeSection === 'pre-script' && (
          <ScriptEditor
            title="Pre-request Script"
            description="Runs before the request is sent. Use this to set headers, modify request data, or run setup logic."
            value={tab.request.scripts?.preRequest || ''}
            onChange={(preRequest) =>
              updateTabRequest(tab.id, {
                scripts: { ...tab.request.scripts, preRequest },
              })
            }
          />
        )}

        {activeSection === 'post-script' && (
          <ScriptEditor
            title="Tests (Post-request Script)"
            description="Runs after the response is received. Use this to write tests, extract data, or set variables."
            value={tab.request.scripts?.postRequest || ''}
            onChange={(postRequest) =>
              updateTabRequest(tab.id, {
                scripts: { ...tab.request.scripts, postRequest },
              })
            }
          />
        )}
      </div>

      {/* Save to Collection Dialog */}
      <SaveToCollectionDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        request={tab.request}
      />
    </div>
  );
}
